import React, { useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  TextField,
  Container,
  CircularProgress
} from '@mui/material';
import {
  AccountBalance
} from '@mui/icons-material';
import { useTheme, alpha } from '@mui/material/styles';
import FileUploadComponent from '../common/FileUploadComponent';

const BankDetailsSection = ({
  bankDraft = {},
  onBankDraftChange,
  validationErrors = {},
  onClearError,
  isSubmitting,
  rejectedDocs
}) => {
  const theme = useTheme();

  const getRejectionError = (docType) => {
    if (!rejectedDocs) return null;
    const doc = rejectedDocs.find(d => d.documentType === docType);
    return doc ? `Rejected: ${doc.rejectionReason}` : null;
  };

  const handleFieldChange = useCallback((field, value) => {
    onBankDraftChange(prev => ({ ...prev, [field]: value }));
    if (onClearError) onClearError(field);
  }, [onBankDraftChange, onClearError]);

  const handleFileUpload = (files) => {
    onBankDraftChange(prev => ({ ...prev, passbookDocument: files }));
    if (onClearError) onClearError('passbookDocument');
  };

  const handleFileRemove = () => {
    onBankDraftChange(prev => ({ ...prev, passbookDocument: [] }));
  };

  // Premium Custom Card Styling
  const customCardStyle = {
    borderRadius: '16px',
    border: '1px solid',
    borderColor: alpha(theme.palette.primary.main, 0.08),
    boxShadow: '0 4px 20px -2px rgba(117, 34, 209, 0.02)',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 12px 30px -4px rgba(117, 34, 209, 0.08)',
      borderColor: alpha(theme.palette.primary.main, 0.2)
    },
    overflow: 'hidden'
  };

  const cardHeaderStyle = (title, icon) => (
    <Box sx={{
      px: { xs: 3, md: 4 },
      py: 3,
      borderBottom: '1px solid',
      borderColor: alpha(theme.palette.primary.main, 0.06),
      bgcolor: alpha(theme.palette.primary.main, 0.02),
      display: 'flex',
      alignItems: 'center',
      gap: 2
    }}>
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 40,
        height: 40,
        borderRadius: '50%',
        bgcolor: alpha(theme.palette.primary.main, 0.08),
        color: 'primary.main'
      }}>
        {icon}
      </Box>
      <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>
        {title}
      </Typography>
    </Box>
  );

  const inputGlobalStyles = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '12px',
      bgcolor: '#F8FAFC',
      transition: 'all 0.2s ease',
      '& fieldset': {
        borderColor: '#E2E8F0',
        transition: 'all 0.2s ease',
      },
      '&:hover fieldset': {
        borderColor: theme.palette.primary.main,
      },
      '&.Mui-focused fieldset': {
        borderColor: theme.palette.primary.main,
      },
      '&.Mui-disabled': {
        bgcolor: alpha(theme.palette.action.disabledBackground, 0.03),
      },
      '&.Mui-disabled fieldset': {
        borderColor: '#F1F5F9',
      },
      '&.Mui-error fieldset': {
        borderColor: theme.palette.error.main,
      },
      '&.Mui-error:hover fieldset': {
        borderColor: theme.palette.error.dark,
      }
    },
    '& .MuiInputLabel-root.Mui-focused': {
      color: theme.palette.primary.main,
    },
    '& .MuiSelect-select': {
      borderRadius: '12px',
    }
  };

  return (
    <Container maxWidth="xl" sx={{ position: 'relative' }}>
      {isSubmitting && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'rgba(255, 255, 255, 0.7)',
            zIndex: 1000,
            borderRadius: '16px',
            backdropFilter: 'blur(4px)',
          }}
        >
          <CircularProgress size={60} thickness={4} sx={{ color: 'primary.main' }} />
        </Box>
      )}
      <Box sx={inputGlobalStyles}>
        {/* Top Welcome Banner Card */}
        <Card elevation={0} sx={{
          mb: 4,
          borderRadius: '16px',
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, #4D1293 100%)`,
          color: '#FFFFFF',
          p: { xs: 3, md: 4 },
          position: 'relative',
          boxShadow: '0 10px 25px -5px rgba(117, 34, 209, 0.3)'
        }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 700, mb: 1, letterSpacing: '-0.5px' }}>
                Bank Details
              </Typography>
              <Typography variant="body1" sx={{ color: alpha('#FFFFFF', 0.8), fontWeight: 400 }}>
                Provide your bank account details for salary processing.
              </Typography>
            </Box>
            <AccountBalance sx={{ fontSize: 80, color: alpha('#FFFFFF', 0.15), display: { xs: 'none', md: 'block' } }} />
          </Box>
        </Card>

        <Card elevation={0} sx={customCardStyle}>
          {cardHeaderStyle("Account Information", <AccountBalance />)}
          <CardContent sx={{ p: { xs: 3, md: 4 } }}>
            <Grid container spacing={{ xs: 2, md: 3 }}>
              <Grid item xs={12} sm={6} md={4} size={{ xs: 12, sm: 6, md: 4 }}>
                <TextField
                  label="Account Holder Name"
                  variant="outlined"
                  value={bankDraft.accountName || ''}
                  onChange={e => handleFieldChange('accountName', e.target.value)}
                  fullWidth
                  required
                  error={!!validationErrors.accountName}
                  helperText={validationErrors.accountName}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4} size={{ xs: 12, sm: 6, md: 4 }}>
                <TextField
                  label="Account Number"
                  variant="outlined"
                  value={bankDraft.accountNumber || ''}
                  onChange={e => handleFieldChange('accountNumber', e.target.value)}
                  fullWidth
                  required
                  error={!!validationErrors.accountNumber}
                  helperText={validationErrors.accountNumber}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4} size={{ xs: 12, sm: 6, md: 4 }}>
                <TextField
                  label="Bank Name"
                  variant="outlined"
                  value={bankDraft.bankName || ''}
                  onChange={e => handleFieldChange('bankName', e.target.value)}
                  fullWidth
                  required
                  error={!!validationErrors.bankName}
                  helperText={validationErrors.bankName}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4} size={{ xs: 12, sm: 6, md: 4 }}>
                <TextField
                  label="IFSC Code"
                  variant="outlined"
                  value={bankDraft.ifscCode || ''}
                  onChange={e => handleFieldChange('ifscCode', e.target.value)}
                  fullWidth
                  required
                  error={!!validationErrors.ifscCode}
                  helperText={validationErrors.ifscCode}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4} size={{ xs: 12, sm: 6, md: 4 }}>
                <TextField
                  label="Branch Name"
                  variant="outlined"
                  value={bankDraft.branchName || ''}
                  onChange={e => handleFieldChange('branchName', e.target.value)}
                  fullWidth
                  required
                  error={!!validationErrors.branchName}
                  helperText={validationErrors.branchName}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: 'text.secondary' }}>
                  Upload Cancellation Check or Bank Passbook *
                </Typography>
                <FileUploadComponent
                  label="Choose File"
                  files={bankDraft.passbookDocument || []}
                  onFileUpload={handleFileUpload}
                  onFileRemove={handleFileRemove}
                  error={!!validationErrors.passbookDocument || !!getRejectionError('Bank Passbook/Cancelled Check')}
                  helperText={getRejectionError('Bank Passbook/Cancelled Check') || validationErrors.passbookDocument || "Please upload a clear scanned copy of your cancelled check or the front page of your passbook (PDF or Image)."}
                  required
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Box>
    </Container>
  );
};

export default BankDetailsSection;
